import { type ReactNode } from 'react';
import { cn } from '../lib/cn';

/**
 * ModernActionTile — a clickable quick-action tile for dashboards / homebases.
 * Icon, label, optional hint, optional trailing badge. Consistent rounded
 * surface, restrained accent only on the icon chip.
 */
export function ModernActionTile({
  icon,
  label,
  hint,
  badge,
  onClick,
  href,
  disabled = false,
  className,
}: {
  icon?: ReactNode;
  label: ReactNode;
  hint?: ReactNode;
  badge?: ReactNode;
  onClick?: () => void;
  href?: string;
  disabled?: boolean;
  className?: string;
}) {
  const content = (
    <>
      <div className="flex items-center justify-between">
        {icon && (
          <span className="flex h-9 w-9 items-center justify-center rounded-[calc(var(--radius)-6px)] bg-primary/[0.08] text-primary">
            {icon}
          </span>
        )}
        {badge && <span className="flex-shrink-0">{badge}</span>}
      </div>
      <div className="mt-3">
        <div className="text-[13px] font-semibold text-foreground">{label}</div>
        {hint && <div className="mt-0.5 text-[12px] leading-relaxed text-muted-foreground">{hint}</div>}
      </div>
    </>
  );

  const classes = cn(
    'group block w-full rounded-[var(--radius)] border border-border bg-card p-4 text-left shadow-[var(--mui-shadow-card)] transition-shadow',
    !disabled &&
      'hover:shadow-[var(--mui-shadow-raised)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    disabled && 'cursor-not-allowed opacity-60',
    className,
  );

  if (href && !disabled) {
    return (
      <a href={href} className={classes}>
        {content}
      </a>
    );
  }

  return (
    <button type="button" onClick={onClick} disabled={disabled} className={classes}>
      {content}
    </button>
  );
}
